import type { IceText } from '@wpt/iceeditor';
import { DownloadOutline } from '@aurora/icons-react';
import {
  Col,
  H2,
  H3,
  Link,
  Paragraph,
  Row,
  Select,
  Spacing,
} from '@aurora/widgets-react';
import moment from 'moment-timezone';
import React, {
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useState,
  FunctionComponent,
} from 'react';
import { Map } from 'react-leaflet';
import useMediaQuery from 'use-media-query-hook';
import { orAbove } from '../../common/breakpointQueries';
import { SEA_DISTRICTS } from '../../common/SeaDistricts';
import { useHashParams } from '../../services/hooks/useHashParam';
import { useTexts } from '../../services/hooks/useTexts';
import { SeaIceMap } from '../MapViewer';
import { SEAICE_GROUPS } from '../MapViewer/MapConfig';
import { TimedGroup } from '../MapViewer/TimedGroup';
import { useTimedLayerGroups } from '../MapViewer/useTimedLayerGroups';
import { IceThicknessLegend } from './IceThicknessLegend';
import { IceTypeLegend } from './IceTypeLegend';
import { useTranslation } from 'react-i18next';
import styles from './SeaIce.scss';

const regions = SEA_DISTRICTS.map((x) => ({ key: x.id, value: x.name }));

export const SeaIceAcronyms = () => {
  const { t } = useTranslation();

  return (
    <>
      <H3>{t('abbreviations')}</H3>
      <Paragraph>{t('nauticalMiles')}</Paragraph>
      <Paragraph>{t('weatherLines')}</Paragraph>
      <Paragraph className={styles.abbrevs}>
        <span>{t('north')}</span>
        <span>{t('south')}</span>
        <span>{t('east')}</span>
        <span>{t('west')}</span>
      </Paragraph>
    </>
  );
};

export interface SeaIceProps {
  mapRef: MutableRefObject<Map | null>;
}

export const SeaIce: FunctionComponent<SeaIceProps> = ({ mapRef }) => {
  const { t, i18n } = useTranslation();

  const { hashParams, setHashParams } = useHashParams();
  const [regionKey, innerSetRegionKey] = useState(regions[0].key);
  const setRegionKey = useCallback(
    (regionKey) => setHashParams({ region: `${regionKey}` }),
    [setHashParams]
  );
  const setRegion = useCallback((region) => setRegionKey(`${region.key}`), []);
  const region = useMemo(
    () => regions.find((r) => r.key === regionKey)!,
    [regionKey]
  );
  const { texts, isLoading } = useTexts();
  const {
    momentTimes,
    times,
    setTimeIndex: innerSetTimeIndex,
    timeIndex,
    setLayerGroup: innerSetLayerGroup,
    layerGroup,
    capabilities,
  } = useTimedLayerGroups(SEAICE_GROUPS);

  useEffect(() => {
    if (hashParams.seaice) {
      if (SEAICE_GROUPS[hashParams.seaice]) {
        innerSetLayerGroup(SEAICE_GROUPS[hashParams.seaice]);
      }
    }
    if (hashParams.region) {
      const regionIndex = regions.findIndex(
        (r) => r.key + '' === hashParams.region
      );
      if (regionIndex !== -1) {
        innerSetRegionKey(regions[regionIndex].key);
      }
    }
  }, [hashParams, innerSetLayerGroup]);

  const setLayerGroup = useCallback(
    (value: TimedGroup) => {
      const index = SEAICE_GROUPS.indexOf(value);
      setHashParams({
        seaice: index.toString(),
      });
    },
    [innerSetLayerGroup, setHashParams]
  );

  const setTimeIndex = useCallback(
    (value) => {
      innerSetTimeIndex(value);
    },
    [innerSetTimeIndex]
  );

  const currentText = useMemo((): IceText | undefined => {
    if (timeIndex == null || !momentTimes || !texts) {
      return undefined;
    }
    const time = momentTimes[timeIndex];
    let best: undefined | IceText = undefined,
      diff = Infinity;
    texts.forEach((text) => {
      let diff2 = time.diff(moment(text.updated));
      if (Math.abs(diff2) < Math.abs(diff)) {
        best = text;
        diff = diff2;
      }
    });
    return best;
  }, [timeIndex, momentTimes, texts]);

  const currentRegionText = useMemo(() => {
    if (currentText && region?.key) {
      return currentText.districts.find((d) => d.id === region.key);
    }
    return undefined;
  }, [currentText, region.key]);

  const isTabletOrBigger = useMediaQuery(orAbove.l);
  const isPhabletOrBigger = useMediaQuery(orAbove.s);

  return (
    <>
      <div style={{ maxWidth: '20rem' }}>
        <Select
          label={t('chooseRegion')}
          value={regionKey}
          onChange={(e) => setRegionKey(e.target.value)}
          multiple={false}
          list={regions}
          placeholder={t('chooseRegion')}
        />
      </div>
      <Row>
        <Col l={7}>
          <SeaIceMap
            layerGroups={SEAICE_GROUPS}
            {...{
              mapRef,
              region,
              setRegion,
              times,
              layerGroup,
              setLayerGroup,
              timeIndex,
              setTimeIndex,
              capabilities,
            }}
          />
          <div aria-live="polite">
            {layerGroup.key === 'seaIceType' ? (
              <>
                <H2>{t('iceType')}</H2>
                <IceTypeLegend layout={isPhabletOrBigger ? 'm' : 's'} />
                <Paragraph>{t('iceTypeParagraph')}</Paragraph>
              </>
            ) : (
              <>
                <H2>{t('iceThickness')}</H2>
                <IceThicknessLegend />
                <Paragraph>{t('iceThicknessParagraph')}</Paragraph>
              </>
            )}
          </div>
          {isTabletOrBigger ? <SeaIceAcronyms /> : null}
        </Col>
        <Col l={5}>
          <H2 aria-live="polite">
            {t('iceConditions', { region: region.value })}
          </H2>
          {currentText ? (
            <div aria-live="polite">
              <Paragraph className={styles.multiLine}>
                {currentRegionText
                  ? currentRegionText[i18n.language]
                  : t('noIce')}
              </Paragraph>
              <Paragraph>
                {t('updated', {
                  time: moment
                    .utc(currentText.updated)
                    .tz('Europe/Stockholm')
                    .format('YYYY-MM-DD HH:mm'),
                })}
              </Paragraph>
            </div>
          ) : (
            <Paragraph>{isLoading ? t('loading') : t('noData')}</Paragraph>
          )}
          <Spacing size="s" />
          <Link to="//www.smhi.se/oceanografi/istjanst/produkter/sstcolor.pdf">
            <DownloadOutline size="s" /> {t('detailedMap')}
          </Link>
          <Spacing size="m" />
          <div className={styles.subtleCard}>
            <H2 noMargin>{t('theIceMap')}</H2>
            <Paragraph>{t('theIceMapParagraph')}</Paragraph>
            <Paragraph>{t('theIceMapParagraph2')}</Paragraph>
            <Paragraph>{t('theIceMapParagraph3')}</Paragraph>
          </div>
          {!isTabletOrBigger ? <SeaIceAcronyms /> : null}
        </Col>
      </Row>
    </>
  );
};
